<template>
  <div class="mt-4">
    <!-- 상단 -->
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense>
        <v-btn icon dark @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>단타의 신 · 게임관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="cyan" dark small class="mr-2" @click="openCreateDialog">
          <v-icon left small>mdi-plus</v-icon>게임 생성
        </v-btn>
        <v-btn color="white" outlined small @click="$router.push('/danta_game_template')">
          <v-icon left small>mdi-cog-outline</v-icon>템플릿 보기
        </v-btn>
      </v-toolbar>
    </v-card>

    <!-- 게임 목록 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-card-title class="py-2">
        <v-icon left small>mdi-gamepad-variant-outline</v-icon>
        <span class="text-subtitle-1 font-weight-bold">게임 목록</span>
        <v-chip v-if="gameList.length > 0" small outlined class="ml-2">{{ gameList.length }}</v-chip>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <div style="overflow-x: auto;">
        <v-data-table
          v-if="gameList.length > 0"
          :headers="gameHeaders"
          :items="gameList"
          :items-per-page="10"
          :footer-props="{'items-per-page-options':[10, 20, -1]}"
          class="elevation-0"
          dense
        >
          <template v-slot:item.icon_url="{ item }">
            <v-img v-if="item.icon_url" :src="s3UrlPrefix + item.icon_url" width="32" height="32" contain style="border-radius: 4px;"></v-img>
            <v-icon v-else small color="grey lighten-2">mdi-image-off-outline</v-icon>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
          </template>
          <template v-slot:item.symbol="{ item }">
            <span class="font-weight-medium">{{ item.symbol_name || item.symbol }}</span>
            <span class="text-caption grey--text ml-1">{{ item.symbol }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn x-small color="blue-grey" dark class="mr-1" @click="openDetailDialog(item)">
              <v-icon left x-small>mdi-information-outline</v-icon>옵션보기
            </v-btn>
            <v-btn v-if="item.status === 'ready'" x-small color="green" dark class="mr-1" @click="changeStatus(item, 'active')">
              <v-icon left x-small>mdi-play</v-icon>시작
            </v-btn>
            <v-btn v-if="item.status === 'active'" x-small color="orange" dark class="mr-1" @click="changeStatus(item, 'paused')">
              <v-icon left x-small>mdi-pause</v-icon>일시정지
            </v-btn>
            <v-btn v-if="item.status === 'paused'" x-small color="green" dark class="mr-1" @click="changeStatus(item, 'active')">
              <v-icon left x-small>mdi-play</v-icon>재개
            </v-btn>
            <v-btn v-if="item.status !== 'ended'" x-small color="red" dark @click="changeStatus(item, 'ended')">
              <v-icon left x-small>mdi-stop</v-icon>종료
            </v-btn>
          </template>
        </v-data-table>
        </div>
        <v-container v-if="gameList.length === 0" class="text-center py-6">
          <v-icon size="40" color="grey lighten-1">mdi-gamepad-variant-outline</v-icon>
          <p class="text-body-2 grey--text mt-2">등록된 게임이 없습니다. "게임 생성"으로 새 게임을 등록하세요.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 게임 생성 다이얼로그 -->
    <v-dialog v-model="createDialog" max-width="600" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left color="cyan">mdi-gamepad-variant</v-icon>
          새 게임 생성
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-select
            v-model="createForm.template_id"
            :items="templateOptions"
            item-text="label"
            item-value="template_id"
            label="게임 템플릿 *"
            outlined dense hide-details class="mb-3"
            :loading="templateLoading"
            @change="onTemplateSelect"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.template_name }} <span class="grey--text text-caption">({{ item.template_code }})</span></v-list-item-title>
                <v-list-item-subtitle>{{ item.data_source }} · {{ gameModeLabel(item.game_mode) }} · {{ formatDuration(item.round_duration) }}주기</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>

          <v-text-field v-model="createForm.title" label="게임 제목 *" outlined dense hide-details class="mb-3" placeholder="비트코인 5분 예측"></v-text-field>

          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="createForm.symbol" label="종목 코드 *" outlined dense hide-details class="mb-3" placeholder="KRW-BTC, 005930"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="createForm.symbol_name" label="종목 이름 *" outlined dense hide-details class="mb-3" placeholder="비트코인, 삼성전자"></v-text-field>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model.number="createForm.bet_amount"
                label="판돈 *"
                outlined dense hide-details class="mb-3"
                type="number"
                placeholder="예: 1000"
                hint="게임 생성 후 수정 불가"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="mb-3 mt-2"></v-divider>

          <!-- ────────── 가격형 운영 설정 (sports / manual 이 아니면 노출) ────────── -->
          <template v-if="!isSportsTemplate && !isManualTemplate">
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small left>mdi-timer-outline</v-icon>라운드 운영 설정
            </div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <v-select
                  v-model="createForm.round_duration"
                  :items="roundDurationOptions"
                  label="라운드 주기 *"
                  outlined dense hide-details class="mb-3"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="createForm.bet_cutoff"
                  label="입찰마감 (종료 N초 전) *"
                  outlined dense hide-details class="mb-3"
                  type="number" min="1"
                  placeholder="예: 30"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="createForm.margin_percent"
                  label="마진(%) *"
                  outlined dense hide-details class="mb-3"
                  type="number" min="0" max="99" step="0.5"
                  placeholder="예: 5"
                  :hint="marginHint"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="createForm.schedule_type"
                  :items="scheduleTypeOptions"
                  label="운영 타입 *"
                  outlined dense hide-details class="mb-3"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch
                  v-model="createForm.round_align_to_clock"
                  inset hide-details dense color="cyan"
                  class="mt-1"
                  label="정시 정렬 (round_duration 배수 시각에만 시작)"
                  hint="5분 주기 → :00/:05/:10/:15 에만 시작. 24h 등 long round 는 자동 무시."
                  persistent-hint
                ></v-switch>
              </v-col>
            </v-row>
            <v-alert dense text type="info" class="mb-4 body-2">
              라운드 {{ formatDuration(createForm.round_duration) }} 중 마지막 {{ createForm.bet_cutoff || 30 }}초 전에 입찰 마감 → 마감 시점 가격을 기준가로 → 종료가와 비교해 승패 판정
            </v-alert>
          </template>

          <!-- ────────── manual 운영 안내 (라운드 단위로 별도 등록) ────────── -->
          <template v-else-if="isManualTemplate">
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small left>mdi-clipboard-edit-outline</v-icon>수동 운영 (퀴즈/정치/이벤트)
            </div>
            <v-alert dense text type="info" class="mb-3 body-2">
              schedule_type 은 자동으로 <strong>manual</strong> 박힙니다. 라운드 운영 설정 (주기 / 입찰마감) 은 무의미합니다.<br>
              <strong>주제 / 선택지 / 베팅 시작·종료</strong> 는 게임 등록 후
              <strong>"수동 운영 라운드"</strong> 메뉴에서 라운드 단위로 등록합니다.<br>
              종료는 [마감] 버튼 — 운영자가 직접.
            </v-alert>
            <v-text-field
              v-model.number="createForm.margin_percent"
              label="마진(%) *"
              outlined dense hide-details class="mb-4"
              type="number" min="0" max="99" step="0.5"
              placeholder="5"
              :hint="marginHint"
              persistent-hint
            ></v-text-field>
          </template>

          <!-- ────────── sports 게임 전용 설정 ────────── -->
          <template v-else-if="isSportsTemplate">
            <div class="text-subtitle-2 font-weight-bold mb-2">
              <v-icon small left>mdi-soccer</v-icon>스포츠 자동화 설정 (API-Football)
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="sportsForm.competition_type"
                  :items="sportsCompetitionOptions"
                  label="대회 형태 *"
                  outlined dense hide-details class="mb-3"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="sportsForm.bet_type"
                  :items="sportsBetTypeOptions"
                  label="베팅 타입 *"
                  outlined dense hide-details class="mb-3"
                ></v-select>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="4" v-if="sportsForm.competition_type === 'league'">
                <v-text-field
                  v-model.number="sportsForm.league_id"
                  label="league_id *"
                  outlined dense hide-details class="mb-3"
                  type="number"
                  placeholder="K1=292, EPL=39, WC=1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" v-if="sportsForm.competition_type === 'national_team'">
                <v-text-field
                  v-model.number="sportsForm.team_id"
                  label="team_id (국대) *"
                  outlined dense hide-details class="mb-3"
                  type="number"
                  placeholder="대한민국=17"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="sportsForm.season"
                  label="season *"
                  outlined dense hide-details class="mb-3"
                  type="number"
                  placeholder="2026"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="sportsForm.bet_close_before_min"
                  label="베팅 마감 (경기 시작 -N분)"
                  hint="예: 5 = 경기 시작 5분 전에 베팅 자동 마감"
                  persistent-hint
                  outlined dense hide-details class="mb-3"
                  type="number" min="1" max="60"
                  placeholder="5"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="sportsForm.choice_group_id"
                  :items="choiceGroups"
                  item-text="label"
                  item-value="group_id"
                  label="선택지 그룹 *"
                  outlined dense hide-details class="mb-3"
                  :loading="choiceGroupsLoading"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" v-if="sportsForm.bet_type === 'over_under'">
                <v-text-field
                  v-model.number="sportsForm.over_under_line"
                  label="오버언더 기준값"
                  outlined dense hide-details class="mb-3"
                  type="number" step="0.5"
                  placeholder="2.5"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="sportsForm.display_name_ko"
                  label="표시명 (한글)"
                  outlined dense hide-details class="mb-3"
                  placeholder="K리그 1"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="createForm.margin_percent"
                  label="마진(%) *"
                  outlined dense hide-details class="mb-3"
                  type="number" min="0" max="99" step="0.5"
                  placeholder="5"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="sportsForm.flag_url"
                  label="국기 URL (선택)"
                  outlined dense hide-details class="mb-3"
                  placeholder="https://media.api-sports.io/flags/kr.svg"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch
                  v-model="sportsForm.venue_neutral_default"
                  inset hide-details dense color="cyan"
                  class="mt-1"
                  label="중립지 default (월드컵 등)"
                  hint="개별 경기 중립지 여부는 sportsScheduleSync 가 venue 정보로 분기. 이 옵션은 game 단위 default."
                  persistent-hint
                ></v-switch>
              </v-col>
            </v-row>

            <!-- sync 윈도우 (sliding) — game 단위 override. 비워두면 dev.js 의 footballApi.syncWindow* 사용 -->
            <div class="text-caption grey--text mb-1">
              <v-icon x-small left>mdi-window-shutter</v-icon>
              sync 윈도우 (sliding) — fixture 가져올 시간 구간 (현재 시점 기준)
            </div>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="sportsForm.sync_window_before_days"
                  label="과거 며칠 (before days)"
                  outlined dense hide-details class="mb-3"
                  type="number" min="0" max="365"
                  placeholder="14"
                  hint="이미 끝난 경기 결과 갱신용. 0 = 미래만 sync"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="sportsForm.sync_window_after_days"
                  label="앞으로 며칠 (after days)"
                  outlined dense hide-details class="mb-3"
                  type="number" min="1" max="365"
                  placeholder="14"
                  hint="신규 라운드 생성 대상. 길수록 미래 경기 미리 노출. 길게 잡고 싶으면 30~90 권장"
                  persistent-hint
                ></v-text-field>
              </v-col>
            </v-row>

            <v-alert dense text type="info" class="mb-4 body-2">
              schedule_type 은 자동으로 <strong>event_driven</strong> 박힘. API-Football v3 가 매 5분 경기 일정 sync (현재 -{{ sportsForm.sync_window_before_days || 14 }}일 ~ +{{ sportsForm.sync_window_after_days || 14 }}일) → 경기 시작 -{{ sportsForm.bet_close_before_min || 5 }}분 자동 베팅 마감 → 경기 종료(FT) 도달 시 자동 정산.
            </v-alert>
          </template>

          <v-textarea v-model="createForm.description" label="설명 (선택)" outlined dense hide-details class="mb-3" rows="2"></v-textarea>
          <v-textarea v-model="createForm.memo" label="운영자 메모 (선택)" outlined dense hide-details class="mb-3" rows="2"></v-textarea>

          <v-divider class="mb-3"></v-divider>
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon small left>mdi-image-multiple-outline</v-icon>이미지 (선택)
          </div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text mb-1 d-block">아이콘 (정사각형)</label>
              <v-img
                v-if="createForm.icon_url"
                :src="s3UrlPrefix + createForm.icon_url"
                height="100"
                contain
                class="mb-2"
                style="border-radius: 8px; border: 1px solid #e0e0e0;"
              />
              <v-file-input
                v-model="createForm.icon_file"
                label="아이콘 선택"
                accept="image/*"
                prepend-icon="mdi-image-outline"
                outlined dense hide-details
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text mb-1 d-block">배너 (와이드)</label>
              <v-img
                v-if="createForm.banner_url"
                :src="s3UrlPrefix + createForm.banner_url"
                height="100"
                contain
                class="mb-2"
                style="border-radius: 8px; border: 1px solid #e0e0e0;"
              />
              <v-file-input
                v-model="createForm.banner_file"
                label="배너 선택"
                accept="image/*"
                prepend-icon="mdi-image-area"
                outlined dense hide-details
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text mb-1 d-block">썸네일 (4:3/16:9)</label>
              <v-img
                v-if="createForm.thumbnail_url"
                :src="s3UrlPrefix + createForm.thumbnail_url"
                height="100"
                contain
                class="mb-2"
                style="border-radius: 8px; border: 1px solid #e0e0e0;"
              />
              <v-file-input
                v-model="createForm.thumbnail_file"
                label="썸네일 선택"
                accept="image/*"
                prepend-icon="mdi-image"
                outlined dense hide-details
              ></v-file-input>
            </v-col>
          </v-row>

          <v-divider class="mb-3 mt-3"></v-divider>
          <v-switch v-model="createForm.start_immediately" inset hide-details class="mt-0">
            <template v-slot:label>
              <span>{{ createForm.start_immediately ? '즉시 시작 (status=active)' : '대기 상태 (status=ready)' }}</span>
            </template>
          </v-switch>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false">취소</v-btn>
          <v-btn color="primary" depressed @click="submitCreate" :loading="createLoading">
            <v-icon left small>mdi-check</v-icon>생성
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 게임 옵션 보기 다이얼로그 -->
    <v-dialog v-model="detailDialog" max-width="800" scrollable>
      <v-card v-if="detailItem">
        <v-card-title class="py-3">
          <v-icon left color="blue-grey">mdi-information-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">게임 옵션 상세</span>
          <v-chip small class="ml-3" :color="statusColor(detailItem.status)" dark>{{ statusLabel(detailItem.status) }}</v-chip>
          <v-spacer></v-spacer>
          <v-btn small text color="primary" class="mr-1" @click="openEditDialog(detailItem)">
            <v-icon small left>mdi-pencil</v-icon>수정
          </v-btn>
          <v-btn icon small @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <!-- 기본 정보 -->
          <div class="text-subtitle-2 font-weight-bold mb-2"><v-icon small left>mdi-card-text-outline</v-icon>기본 정보</div>
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr><td class="grey--text" style="width:160px">게임 ID</td><td>{{ detailItem.game_id }}</td></tr>
              <tr><td class="grey--text">게임명</td><td class="font-weight-medium">{{ detailItem.title }}</td></tr>
              <tr><td class="grey--text">종목</td><td>{{ detailItem.symbol_name || '-' }} <span class="grey--text text-caption ml-1">{{ detailItem.symbol }}</span></td></tr>
              <tr><td class="grey--text">템플릿</td><td>{{ detailItem.template_name || '-' }} <span class="grey--text text-caption ml-1">(ID: {{ detailItem.template_id || '-' }})</span></td></tr>
              <tr><td class="grey--text">데이터 소스</td><td>{{ detailItem.data_source || '-' }}</td></tr>
              <tr><td class="grey--text">게임 모드</td><td>{{ gameModeLabel(detailItem.game_mode) }}</td></tr>
            </tbody>
          </v-simple-table>

          <!-- 운영 설정 -->
          <div class="text-subtitle-2 font-weight-bold mb-2"><v-icon small left>mdi-cog-outline</v-icon>운영 설정</div>
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr><td class="grey--text" style="width:160px">판돈 (bet_amount)</td><td>{{ formatNumber(detailItem.bet_amount) }}</td></tr>
              <tr><td class="grey--text">라운드 주기</td><td>{{ formatDuration(detailItem.round_duration) }} <span class="grey--text text-caption ml-1">({{ detailItem.round_duration }}초)</span></td></tr>
              <tr><td class="grey--text">입찰 마감</td><td>종료 {{ detailItem.bet_cutoff || '-' }}초 전</td></tr>
              <tr><td class="grey--text">마진</td><td>{{ marginText(detailItem.margin) }}</td></tr>
              <tr><td class="grey--text">운영 타입</td><td>{{ scheduleLabel(detailItem.schedule_type) }}</td></tr>
            </tbody>
          </v-simple-table>

          <!-- 라운드 정보 -->
          <div class="text-subtitle-2 font-weight-bold mb-2"><v-icon small left>mdi-timer-outline</v-icon>라운드 정보</div>
          <v-simple-table dense class="mb-4">
            <tbody>
              <tr><td class="grey--text" style="width:160px">현재 회차</td><td>{{ detailItem.current_round_id || '-' }}</td></tr>
              <tr><td class="grey--text">총 회차</td><td>{{ detailItem.total_rounds || 0 }}</td></tr>
              <tr v-if="detailItem.create_dt"><td class="grey--text">생성일</td><td>{{ detailItem.create_dt }}</td></tr>
              <tr v-if="detailItem.update_dt"><td class="grey--text">수정일</td><td>{{ detailItem.update_dt }}</td></tr>
            </tbody>
          </v-simple-table>

          <!-- 설명 / 메모 -->
          <div v-if="detailItem.description || detailItem.memo">
            <div class="text-subtitle-2 font-weight-bold mb-2"><v-icon small left>mdi-note-text-outline</v-icon>설명 / 메모</div>
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr v-if="detailItem.description"><td class="grey--text" style="width:160px">설명</td><td style="white-space:pre-wrap">{{ detailItem.description }}</td></tr>
                <tr v-if="detailItem.memo"><td class="grey--text">운영자 메모</td><td style="white-space:pre-wrap">{{ detailItem.memo }}</td></tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- 이미지 -->
          <div v-if="detailItem.icon_url || detailItem.banner_url || detailItem.thumbnail_url">
            <div class="text-subtitle-2 font-weight-bold mb-2"><v-icon small left>mdi-image-multiple-outline</v-icon>이미지</div>
            <v-row dense>
              <v-col v-if="detailItem.icon_url" cols="4">
                <label class="text-caption grey--text mb-1 d-block">아이콘</label>
                <v-img :src="s3UrlPrefix + detailItem.icon_url" height="120" contain style="border-radius:8px; border:1px solid #e0e0e0;" />
              </v-col>
              <v-col v-if="detailItem.banner_url" cols="4">
                <label class="text-caption grey--text mb-1 d-block">배너</label>
                <v-img :src="s3UrlPrefix + detailItem.banner_url" height="120" contain style="border-radius:8px; border:1px solid #e0e0e0;" />
              </v-col>
              <v-col v-if="detailItem.thumbnail_url" cols="4">
                <label class="text-caption grey--text mb-1 d-block">썸네일</label>
                <v-img :src="s3UrlPrefix + detailItem.thumbnail_url" height="120" contain style="border-radius:8px; border:1px solid #e0e0e0;" />
              </v-col>
            </v-row>
          </div>

          <!-- Raw JSON (디버그용) -->
          <v-expansion-panels flat class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="text-caption grey--text">Raw JSON 보기</v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="text-caption" style="white-space:pre-wrap; background:#fafafa; padding:8px; border-radius:4px;">{{ detailItemJson }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailDialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 게임 수정 다이얼로그 — 메타 + margin + 운영 파라미터 -->
    <v-dialog v-model="editDialog" max-width="640" persistent scrollable>
      <v-card v-if="editForm">
        <v-card-title class="py-3">
          <v-icon left color="primary">mdi-pencil</v-icon>
          <span class="text-subtitle-1 font-weight-bold">게임 수정 (ID: {{ editForm.game_id }})</span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="closeEditDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row dense>
            <v-col cols="12">
              <v-text-field outlined dense hide-details label="게임명" v-model="editForm.title" maxlength="200"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea outlined dense hide-details label="설명" v-model="editForm.description" rows="2" auto-grow></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-textarea outlined dense hide-details label="운영자 메모" v-model="editForm.memo" rows="2" auto-grow></v-textarea>
            </v-col>
            <v-col cols="6">
              <v-text-field outlined dense hide-details type="number" label="판돈 (bet_amount)" v-model.number="editForm.bet_amount" min="0"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field outlined dense hide-details type="number" label="라운드 주기 (초)" v-model.number="editForm.round_duration" min="5" max="86400"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field outlined dense hide-details type="number" label="입찰 마감 (종료 X초 전)" v-model.number="editForm.bet_cutoff" min="1" max="86400"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select outlined dense hide-details label="운영 타입" :items="scheduleOptions" item-text="text" item-value="value" v-model="editForm.schedule_type"></v-select>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="editForm.round_align_to_clock"
                inset hide-details dense color="cyan"
                class="mt-0"
                label="정시 정렬 (round_duration 배수 시각에만 시작)"
              ></v-switch>
              <div class="text-caption grey--text ml-2 mb-2">
                5분 주기 → :00/:05/:10/:15 에만 시작. 24h 등 long round 는 자동 무시.
              </div>
            </v-col>
            <v-col cols="12">
              <v-text-field outlined dense type="number" :step="0.1" min="0" max="99"
                label="마진 (%) — 환불 라운드엔 적용 안 됨"
                v-model.number="editForm.margin_percent"
                :hint="editMarginHint" persistent-hint></v-text-field>
            </v-col>

            <!-- sports (event_driven) 전용 — fixture sync 윈도우.
                 game.config.sync_window_* 에 박힘. 빈 값이면 서버 default (dev.js syncWindow* = 14/14). -->
            <v-col cols="12" v-if="editForm.schedule_type === 'event_driven'">
              <v-divider class="mb-3"></v-divider>
              <div class="text-caption grey--text mb-2">
                <v-icon x-small left>mdi-window-shutter</v-icon>
                sync 윈도우 (sliding) — fixture 가져올 시간 구간 (현재 시점 기준). 비워두면 서버 default 사용.
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field outlined dense type="number" min="0" max="365"
                    label="과거 며칠 (before days)"
                    placeholder="14"
                    v-model.number="editForm.sync_window_before_days"
                    hint="이미 끝난 경기 결과 갱신용. 0 = 미래만 sync"
                    persistent-hint
                    clearable
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field outlined dense type="number" min="1" max="365"
                    label="앞으로 며칠 (after days)"
                    placeholder="14"
                    v-model.number="editForm.sync_window_after_days"
                    hint="신규 라운드 생성 대상. 길수록 미래 경기 미리 노출"
                    persistent-hint
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>

            <!-- 이미지 3종 (선택 — 새로 고른 파일만 S3 업로드) -->
            <v-col cols="12" class="mt-2">
              <v-divider class="mb-3"></v-divider>
              <div class="text-subtitle-2 font-weight-bold mb-2">
                <v-icon small left>mdi-image-multiple-outline</v-icon>이미지 (선택 — 변경할 항목만 새로 업로드)
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text mb-1 d-block">아이콘 (정사각형)</label>
              <v-img
                v-if="editForm.icon_url"
                :src="s3UrlPrefix + editForm.icon_url"
                height="100" contain class="mb-2"
                style="border-radius: 8px; border: 1px solid #e0e0e0;"
              />
              <v-file-input
                v-model="editForm.icon_file"
                label="아이콘 선택"
                accept="image/*"
                prepend-icon="mdi-image-outline"
                outlined dense hide-details show-size
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text mb-1 d-block">배너 (와이드)</label>
              <v-img
                v-if="editForm.banner_url"
                :src="s3UrlPrefix + editForm.banner_url"
                height="100" contain class="mb-2"
                style="border-radius: 8px; border: 1px solid #e0e0e0;"
              />
              <v-file-input
                v-model="editForm.banner_file"
                label="배너 선택"
                accept="image/*"
                prepend-icon="mdi-image-area"
                outlined dense hide-details show-size
              ></v-file-input>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="text-caption grey--text mb-1 d-block">썸네일 (4:3/16:9)</label>
              <v-img
                v-if="editForm.thumbnail_url"
                :src="s3UrlPrefix + editForm.thumbnail_url"
                height="100" contain class="mb-2"
                style="border-radius: 8px; border: 1px solid #e0e0e0;"
              />
              <v-file-input
                v-model="editForm.thumbnail_file"
                label="썸네일 선택"
                accept="image/*"
                prepend-icon="mdi-image"
                outlined dense hide-details show-size
              ></v-file-input>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <span v-if="editError" class="red--text text-caption ml-2">{{ editError }}</span>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEditDialog" :disabled="editSaving">취소</v-btn>
          <v-btn color="primary" :loading="editSaving" @click="saveEdit">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
const { FileUpload_Async_SnapService } = require('../../util/fileutil');
// 단타의 신 게임 자산은 snap-service 버킷을 사용한다 (다른 패키지와 분리)
const S3_URL_PREFIX = 'https://snap-service.s3.ap-northeast-2.amazonaws.com/';
export default {
  name: 'Danta_GameManage',
  data: () => ({
    s3UrlPrefix: S3_URL_PREFIX,
    gameList: [],
    detailDialog: false,
    detailItem: null,
    createDialog: false,
    createLoading: false,
    templateLoading: false,
    templateOptions: [],
    createForm: {
      template_id: null,
      title: '',
      symbol: '',
      symbol_name: '',
      description: '',
      memo: '',
      bet_amount: null,
      round_duration: 300,
      bet_cutoff: 30,
      schedule_type: '24h',
      margin_percent: 5,
      // 정시 정렬 — 신규 게임 기본 ON. round_duration 배수 정시 (5분 주기 → :00/:05/:10) 에만 라운드 시작.
      // long round (24h 등) 는 엔진에서 자동 무시.
      round_align_to_clock: true,
      start_immediately: true,
      icon_file: null,
      icon_url: '',
      banner_file: null,
      banner_url: '',
      thumbnail_file: null,
      thumbnail_url: '',
    },
    roundDurationOptions: [
      { text: '1분', value: 60 }, { text: '3분', value: 180 }, { text: '5분', value: 300 },
      { text: '10분', value: 600 }, { text: '30분', value: 1800 }, { text: '1시간', value: 3600 },
      { text: '4시간', value: 14400 }, { text: '24시간', value: 86400 },
    ],
    scheduleTypeOptions: [
      { text: '24시간 운영', value: '24h' },
      { text: '한국 장 시간', value: 'kr_market' },
      { text: '미국 장 시간', value: 'us_market' },
      { text: '외부 이벤트 (sports/quiz)', value: 'event_driven' },
      { text: '수동 운영 (퀴즈/정치/이벤트)', value: 'manual' },
    ],
    // sports 게임 전용 입력 (game_mode='sports' 선택 시 노출)
    sportsForm: {
      choice_group_id:        null,
      competition_type:       'league',   // 'league' | 'national_team'
      league_id:              null,
      team_id:                null,
      season:                 new Date().getUTCFullYear(),
      bet_type:               'win_lose', // 'win_lose' | 'scoreline' | 'over_under' | 'btts'
      over_under_line:        2.5,
      display_name_ko:        '',
      venue_neutral_default:  false,
      bet_close_before_min:   5,
      flag_url:               '',
      // sync 윈도우 (sliding) — 게임 단위 override. 비워두면 dev.js 의 footballApi.syncWindow* 사용.
      sync_window_before_days: 14,
      sync_window_after_days:  14,
    },
    // 'national_team' 은 단발이 아닌 시즌형 — league_id 가 아니라 team_id 로 호출하는 차이만 있음.
    //   sportsScheduleSync 가 같은 4개월 윈도우로 매 A매치 = 1 round 사이클 (K리그/EPL 과 동일).
    sportsCompetitionOptions: [
      { text: '리그 (월드컵/EPL/K리그 등)', value: 'league' },
      { text: '국가대표 (team_id 단위)',    value: 'national_team' },
    ],
    sportsBetTypeOptions: [
      { text: '승무패 (1X2)',         value: 'win_lose' },
      { text: '정확한 스코어',         value: 'scoreline' },
      { text: '오버언더 (총득점)',     value: 'over_under' },
      { text: '양팀 득점 (BTTS)',     value: 'btts' },
    ],
    choiceGroups: [],
    choiceGroupsLoading: false,
    gameHeaders: [
      { text: 'ID', value: 'game_id', width: '60px' },
      { text: '아이콘', value: 'icon_url', width: '60px', sortable: false, align: 'center' },
      { text: '게임명', value: 'title', width: '180px' },
      { text: '종목', value: 'symbol', width: '140px' },
      { text: '템플릿', value: 'template_name', width: '100px' },
      { text: '현재회차', value: 'current_round_id', width: '80px' },
      { text: '총회차', value: 'total_rounds', width: '70px' },
      { text: '상태', value: 'status', width: '90px' },
      { text: '관리', value: 'actions', width: '300px', sortable: false },
    ],
    // ── 게임 수정 다이얼로그 상태 ──
    editDialog: false,
    editSaving: false,
    editError: '',
    editForm: null,
    scheduleOptions: [
      { text: '24시간 운영', value: '24h' },
      { text: '한국 장 시간', value: 'kr_market' },
      { text: '미국 장 시간', value: 'us_market' },
      { text: '외부 이벤트 (sports/quiz)', value: 'event_driven' },
      { text: '수동 운영 (퀴즈/정치/이벤트)', value: 'manual' },
    ],
  }),
  computed: {
    /** 현재 선택된 템플릿 객체 */
    selectedTemplate() {
      if (!this.createForm.template_id) return null;
      return this.templateOptions.find(t => t.template_id === this.createForm.template_id) || null;
    },
    /** sports 모드 여부 — 가격형 운영 설정 숨기고 sports 섹션 노출 */
    isSportsTemplate() {
      return this.selectedTemplate && this.selectedTemplate.game_mode === 'sports';
    },
    /** manual 모드 여부 — 가격형 운영 설정 숨기고 manual 안내만 노출.
     *  라운드 단위 등록/마감/결과는 "수동 운영 라운드" 페이지(Manual_Round_Manage)에서 처리. */
    isManualTemplate() {
      return this.selectedTemplate && this.selectedTemplate.game_mode === 'manual';
    },
    /** 마진 입력값에 따른 안내 문구 — 실 지급 배수 (= 2 - margin) 미리 보기 */
    marginHint() {
      const pct = Number(this.createForm.margin_percent);
      if (!Number.isFinite(pct) || pct < 0 || pct >= 100) {
        return '0 ~ 99 사이 입력. 5 = 5% 마진 (이긴 사람 딴 돈에서 5% 차감)';
      }
      const multiplier = (2 - pct / 100).toFixed(2);
      return `이긴 사람에게 베팅의 ${multiplier}배 지급 (마진 ${pct}%)`;
    },
    detailItemJson() {
      return this.detailItem ? JSON.stringify(this.detailItem, null, 2) : '';
    },
    editMarginHint() {
      if (!this.editForm) return '';
      const pct = Number(this.editForm.margin_percent);
      if (!Number.isFinite(pct) || pct < 0 || pct >= 100) {
        return '0 ~ 99 사이. 5 = 5% 마진 (이긴 사람 딴 돈에서 5% 차감). 환불 라운드(draw/cancelled)엔 적용 안 됨.';
      }
      const multiplier = (2 - pct / 100).toFixed(2);
      return `이긴 사람에게 베팅의 ${multiplier}배 지급 (마진 ${pct}%) — 환불 라운드엔 적용 안 됨`;
    },
  },
  created() {
    this.loadGameList();
  },
  methods: {
    ...mapActions([
      'DANTA_GET_GAME_LIST',
      'DANTA_GET_GAME_TEMPLATE_LIST',
      'DANTA_CREATE_GAME',
      'DANTA_UPDATE_GAME_STATUS',
      'DANTA_UPDATE_GAME',
      'DANTA_GET_CHOICE_GROUPS',
    ]),

    // ── 게임 수정 다이얼로그 ──
    openEditDialog(item) {
      if (!item) return;
      const cfg = (item.config && typeof item.config === 'object') ? item.config : {};
      this.editForm = {
        game_id:        item.game_id,
        title:          item.title || '',
        description:    item.description || '',
        memo:           item.memo || '',
        bet_amount:     item.bet_amount != null ? Number(item.bet_amount) : null,
        round_duration: item.round_duration != null ? Number(item.round_duration) : 300,
        bet_cutoff:     item.bet_cutoff     != null ? Number(item.bet_cutoff)     : 30,
        schedule_type:  item.schedule_type || '24h',
        round_align_to_clock: !!item.round_align_to_clock,
        // margin 은 0~0.99 소수 — UI 에서는 % 로 다룸
        margin_percent: item.margin != null ? Number((Number(item.margin) * 100).toFixed(2)) : 5,
        // 이미지: 기존 S3 키 + 새로 고른 파일 후보 (둘 다 null/'' 가능)
        icon_url:        item.icon_url || '',
        banner_url:      item.banner_url || '',
        thumbnail_url:   item.thumbnail_url || '',
        icon_file:       null,
        banner_file:     null,
        thumbnail_file:  null,
        // sports (event_driven) 만 사용 — fixture sync 윈도우 (sliding).
        //   beforeDays/afterDays 가 game.config 에 박혀있으면 그 값,
        //   없으면 빈 값(서버 default = environments/dev.js 의 syncWindow*).
        sync_window_before_days: Number(cfg.sync_window_before_days) >= 0 ? Number(cfg.sync_window_before_days) : null,
        sync_window_after_days:  Number(cfg.sync_window_after_days)  > 0  ? Number(cfg.sync_window_after_days)  : null,
        // 원본 config 보존 — sports 의 league_id / season / competition_type 등 다른 필드 유지 위해.
        _origConfig: cfg,
      };
      this.editError = '';
      this.editDialog = true;
    },
    closeEditDialog() {
      if (this.editSaving) return;
      this.editDialog = false;
      this.editForm = null;
      this.editError = '';
    },
    async saveEdit() {
      if (!this.editForm || !this.editForm.game_id) return;
      const pct = Number(this.editForm.margin_percent);
      if (!Number.isFinite(pct) || pct < 0 || pct >= 100) {
        this.editError = '마진은 0 ~ 99 사이로 입력하세요';
        return;
      }
      this.editSaving = true;
      this.editError = '';
      try {
        // 새 이미지가 선택된 항목만 S3 업로드 (createDialog 와 동일한 키 prefix 사용)
        let iconUrl       = this.editForm.icon_url      || '';
        let bannerUrl     = this.editForm.banner_url    || '';
        let thumbnailUrl  = this.editForm.thumbnail_url || '';
        if (this.editForm.icon_file) {
          iconUrl = 'danta/games/icon/' + this.Utill_CreateNewImageName(this.editForm.icon_file.name);
          await FileUpload_Async_SnapService(this.editForm.icon_file, iconUrl);
        }
        if (this.editForm.banner_file) {
          bannerUrl = 'danta/games/banner/' + this.Utill_CreateNewImageName(this.editForm.banner_file.name);
          await FileUpload_Async_SnapService(this.editForm.banner_file, bannerUrl);
        }
        if (this.editForm.thumbnail_file) {
          thumbnailUrl = 'danta/games/thumbnail/' + this.Utill_CreateNewImageName(this.editForm.thumbnail_file.name);
          await FileUpload_Async_SnapService(this.editForm.thumbnail_file, thumbnailUrl);
        }

        const payload = {
          game_id:        this.editForm.game_id,
          title:          this.editForm.title,
          description:    this.editForm.description,
          memo:           this.editForm.memo,
          bet_amount:     this.editForm.bet_amount,
          round_duration: this.editForm.round_duration,
          bet_cutoff:     this.editForm.bet_cutoff,
          schedule_type:  this.editForm.schedule_type,
          round_align_to_clock: !!this.editForm.round_align_to_clock,
          // 소수로 변환해서 전송 (서버 CHECK: 0 <= margin < 1)
          margin:         Number((pct / 100).toFixed(4)),
          // 이미지 URL — 변경되지 않았어도 그대로 같이 보내야 백엔드 동적 SET 에서 다른 필드와 함께 갱신됨
          // (백엔드는 undefined 만 skip, ''/null 은 빈 값으로 갱신)
          icon_url:       iconUrl,
          banner_url:     bannerUrl,
          thumbnail_url:  thumbnailUrl,
        };

        // event_driven (sports) — config 갱신.
        // _origConfig 를 기반으로 sync_window_* 만 덮어쓰기 (league_id/season/competition_type 등 보존).
        // 빈 값이면 key 자체 제거 → engine 이 dev.js default 사용.
        if (this.editForm.schedule_type === 'event_driven') {
          const nextCfg = { ...(this.editForm._origConfig || {}) };
          const beforeN = Number(this.editForm.sync_window_before_days);
          const afterN  = Number(this.editForm.sync_window_after_days);
          if (Number.isFinite(beforeN) && beforeN >= 0) nextCfg.sync_window_before_days = beforeN;
          else delete nextCfg.sync_window_before_days;
          if (Number.isFinite(afterN)  && afterN  > 0) nextCfg.sync_window_after_days  = afterN;
          else delete nextCfg.sync_window_after_days;
          payload.config = nextCfg;
        }

        await this.DANTA_UPDATE_GAME(payload);
        this.editDialog = false;
        this.editForm = null;
        // 목록 + 상세 다시 로드 (있다면)
        await this.loadGameList();
        if (this.detailItem && this.detailItem.game_id === payload.game_id) {
          // 상세 dialog 도 표시값 갱신 — game_id 로 다시 조회는 별도 액션이 있으면 호출, 없으면 그냥 닫음
          this.detailDialog = false;
        }
      } catch (err) {
        this.editError = (err && (err.message || err.err_message)) || String(err);
        console.error('[Game_Manage] update_game 실패:', err);
      } finally {
        this.editSaving = false;
      }
    },
    async loadGameList() {
      try {
        const [readyData, activeData, pausedData] = await Promise.all([
          this.DANTA_GET_GAME_LIST({ status: 'ready', page: 1, page_size: 50 }),
          this.DANTA_GET_GAME_LIST({ status: 'active', page: 1, page_size: 50 }),
          this.DANTA_GET_GAME_LIST({ status: 'paused', page: 1, page_size: 50 }),
        ]);
        this.gameList = [
          ...(readyData.game_list || []),
          ...(activeData.game_list || []),
          ...(pausedData.game_list || []),
        ];
      } catch (err) { console.error('게임 목록 로드 실패:', err); }
    },
    async openCreateDialog() {
      this.createForm = {
        template_id: null, title: '', symbol: '', symbol_name: '',
        description: '', memo: '',
        bet_amount: null,
        round_duration: 300, bet_cutoff: 30, schedule_type: '24h', margin_percent: 5,
        start_immediately: true,
        icon_file: null, icon_url: '',
        banner_file: null, banner_url: '',
        thumbnail_file: null, thumbnail_url: '',
      };
      // sports 폼 초기화
      this.sportsForm = {
        choice_group_id: null, competition_type: 'league',
        league_id: null, team_id: null,
        season: new Date().getUTCFullYear(),
        bet_type: 'win_lose', over_under_line: 2.5,
        display_name_ko: '', venue_neutral_default: false,
        bet_close_before_min: 5, flag_url: '',
        sync_window_before_days: 14,
        sync_window_after_days:  14,
      };
      this.createDialog = true;
      if (this.templateOptions.length === 0) {
        this.templateLoading = true;
        try {
          const data = await this.DANTA_GET_GAME_TEMPLATE_LIST();
          this.templateOptions = (data.template_list || []).filter(t => t.is_active).map(t => ({
            ...t,
            label: `${t.template_name} (${t.template_code})`
          }));
        } catch (err) { console.error(err); }
        finally { this.templateLoading = false; }
      }
      // sports 선택지 그룹 (재로드 안 함 — 이미 있으면 유지)
      if (this.choiceGroups.length === 0) {
        this.loadChoiceGroups();
      }
    },
    async loadChoiceGroups() {
      this.choiceGroupsLoading = true;
      try {
        const r = await this.DANTA_GET_CHOICE_GROUPS({});
        this.choiceGroups = (r?.groups || []).map(g => ({
          ...g,
          label: `${g.group_name} (${g.group_code}) — ${(g.choices || []).map(c => c.code).join('/')}`,
        }));
      } catch (e) {
        console.error('choice_groups 로드 실패:', e);
      } finally {
        this.choiceGroupsLoading = false;
      }
    },
    onTemplateSelect(templateId) {
      const tpl = this.templateOptions.find(t => t.template_id === templateId);
      if (tpl) {
        this.createForm.symbol = tpl.symbol || '';
        this.createForm.symbol_name = tpl.symbol_name || '';
        this.createForm.title = `${tpl.template_name} 예측`;
        // sports 템플릿 선택 시 — 기본 schedule_type 변경 + symbol 자동
        if (tpl.game_mode === 'sports') {
          this.createForm.schedule_type = 'event_driven';
          if (!this.createForm.symbol) this.createForm.symbol = `${tpl.template_code.toUpperCase()}_WL`;
          if (!this.createForm.symbol_name) this.createForm.symbol_name = tpl.template_name;
        }
      }
    },
    async submitCreate() {
      if (!this.createForm.template_id || !this.createForm.title || !this.createForm.symbol) {
        alert('템플릿, 게임 제목, 종목 코드는 필수입니다.');
        return;
      }
      if (!this.createForm.bet_amount || this.createForm.bet_amount <= 0) {
        alert('판돈은 필수이며 0보다 커야 합니다.');
        return;
      }
      const marginPercent = Number(this.createForm.margin_percent);
      if (!Number.isFinite(marginPercent) || marginPercent < 0 || marginPercent >= 100) {
        alert('마진은 0% 이상 100% 미만이어야 합니다.'); return;
      }
      const margin = Number((marginPercent / 100).toFixed(4));

      // ── sports / manual 게임 — 가격형 검증 우회 + 각 모드별 config 처리 ──
      const isSports = this.isSportsTemplate;
      const isManual = this.isManualTemplate;
      let roundDuration, betCutoff, scheduleType, configPayload, choiceGroupId;

      if (isSports) {
        const sf = this.sportsForm;
        if (!sf.choice_group_id)             { alert('선택지 그룹을 선택해주세요.'); return; }
        if (!sf.season)                       { alert('season 은 필수입니다.'); return; }
        if (sf.competition_type === 'league' && !sf.league_id) { alert('league_id 필수.'); return; }
        if (sf.competition_type === 'national_team' && !sf.team_id) { alert('team_id 필수.'); return; }

        roundDuration = 300;   // sports 에선 무의미 (NOT NULL 회피용 default)
        betCutoff     = 30;
        scheduleType  = 'event_driven';
        choiceGroupId = sf.choice_group_id;
        configPayload = {
          competition_type:       sf.competition_type,
          season:                 sf.season,
          bet_type:               sf.bet_type,
          display_name_ko:        sf.display_name_ko || this.createForm.title,
          icon_url:               this.createForm.icon_url || '',
          flag_url:               sf.flag_url || '',
          venue_neutral_default:  !!sf.venue_neutral_default,
          bet_close_before_min:   Number(sf.bet_close_before_min) || 5,
          // sync 윈도우 (sliding) — 0/빈값은 박지 않음 → engine 이 dev.js 전역 default 사용
          ...(Number(sf.sync_window_before_days) >= 0 ? { sync_window_before_days: Number(sf.sync_window_before_days) } : {}),
          ...(Number(sf.sync_window_after_days)  > 0  ? { sync_window_after_days:  Number(sf.sync_window_after_days)  } : {}),
        };
        if (sf.competition_type === 'league')         configPayload.league_id = sf.league_id;
        if (sf.competition_type === 'national_team')  configPayload.team_id   = sf.team_id;
        if (sf.bet_type === 'over_under')             configPayload.over_under_line = Number(sf.over_under_line) || 2.5;
      } else if (isManual) {
        // manual 게임 — 라운드 운영 설정 무시. schedule_type 강제 'manual'. 라운드 단위로 별도 등록.
        roundDuration = 300;   // NOT NULL 회피용 default. 의미 없음 (라운드는 운영자가 직접 등록/마감).
        betCutoff     = 30;
        scheduleType  = 'manual';
        choiceGroupId = null;  // 게임 단 default choice_group 안 박음 — 라운드마다 다른 그룹 선택 가능.
        configPayload = { manual: true };
      } else {
        roundDuration = Number(this.createForm.round_duration) || 0;
        betCutoff     = Number(this.createForm.bet_cutoff) || 0;
        scheduleType  = this.createForm.schedule_type;
        choiceGroupId = null;
        configPayload = null;

        if (!roundDuration || roundDuration < 5) { alert('라운드 주기는 5초 이상이어야 합니다.'); return; }
        if (!betCutoff || betCutoff < 1 || betCutoff >= roundDuration) {
          alert('입찰마감은 1초 이상이며 라운드 주기보다 작아야 합니다.'); return;
        }
        const validSchedules = ['24h', 'kr_market', 'us_market'];
        if (!validSchedules.includes(scheduleType)) {
          alert('운영 타입을 선택해주세요.'); return;
        }
      }
      this.createLoading = true;
      try {
        let icon_url = this.createForm.icon_url || '';
        let banner_url = this.createForm.banner_url || '';
        let thumbnail_url = this.createForm.thumbnail_url || '';
        if (this.createForm.icon_file) {
          icon_url = 'danta/games/icon/' + this.Utill_CreateNewImageName(this.createForm.icon_file.name);
          await FileUpload_Async_SnapService(this.createForm.icon_file, icon_url);
        }
        if (this.createForm.banner_file) {
          banner_url = 'danta/games/banner/' + this.Utill_CreateNewImageName(this.createForm.banner_file.name);
          await FileUpload_Async_SnapService(this.createForm.banner_file, banner_url);
        }
        if (this.createForm.thumbnail_file) {
          thumbnail_url = 'danta/games/thumbnail/' + this.Utill_CreateNewImageName(this.createForm.thumbnail_file.name);
          await FileUpload_Async_SnapService(this.createForm.thumbnail_file, thumbnail_url);
        }

        await this.DANTA_CREATE_GAME({
          template_id: this.createForm.template_id,
          title: this.createForm.title,
          symbol: this.createForm.symbol,
          symbol_name: this.createForm.symbol_name,
          description: this.createForm.description,
          memo: this.createForm.memo,
          bet_amount: this.createForm.bet_amount,
          round_duration: roundDuration,
          bet_cutoff: betCutoff,
          schedule_type: scheduleType,
          margin,
          round_align_to_clock: this.createForm.round_align_to_clock,
          status: this.createForm.start_immediately ? 'active' : 'ready',
          icon_url,
          banner_url,
          thumbnail_url,
          // sports 전용 — config jsonb + choice_group_id
          ...(configPayload ? { config: configPayload } : {}),
          ...(choiceGroupId ? { choice_group_id: choiceGroupId } : {}),
        });
        this.createDialog = false;
        await this.loadGameList();
      } catch (err) {
        console.error(err);
        alert('게임 생성에 실패했습니다.');
      } finally {
        this.createLoading = false;
      }
    },
    Utill_CreateNewImageName(originalFileName) {
      const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));
      const randomString = Math.random().toString(36).substring(2, 18);
      return randomString + fileExtension;
    },
    async changeStatus(item, newStatus) {
      const labels = { active: '시작', paused: '일시정지', ended: '종료' };
      if (!confirm(`[${item.title}]을 ${labels[newStatus] || newStatus}하시겠습니까?`)) return;
      try {
        await this.DANTA_UPDATE_GAME_STATUS({ game_id: item.game_id, status: newStatus });
        await this.loadGameList();
      } catch (err) {
        console.error(err);
        alert('상태 변경에 실패했습니다.');
      }
    },
    statusColor(status) {
      const map = { active: 'green', paused: 'orange', ready: 'blue-grey', ended: 'grey', cancelled: 'red' };
      return map[status] || 'grey';
    },
    statusLabel(status) {
      const map = { active: '활성', paused: '일시정지', ready: '대기', ended: '종료', cancelled: '취소' };
      return map[status] || status;
    },
    gameModeLabel(mode) {
      const map = { price: '가격예측', sports: '스포츠', custom: '커스텀' };
      return map[mode] || mode || '-';
    },
    formatDuration(sec) {
      if (!sec) return '-';
      if (sec < 60) return sec + '초';
      if (sec < 3600) return (sec / 60) + '분';
      if (sec < 86400) return (sec / 3600) + '시간';
      return (sec / 86400) + '일';
    },
    openDetailDialog(item) {
      this.detailItem = item;
      this.detailDialog = true;
    },
    formatNumber(n) {
      if (n == null || n === '') return '-';
      const num = Number(n);
      return Number.isFinite(num) ? num.toLocaleString() : String(n);
    },
    marginText(margin) {
      const m = Number(margin);
      if (!Number.isFinite(m)) return '-';
      const pct = (m * 100).toFixed(2).replace(/\.?0+$/, '');
      const multiplier = (2 - m).toFixed(2);
      return `${pct}% (배당 배수 ${multiplier}배)`;
    },
    scheduleLabel(s) {
      const map = { '24h': '24시간 운영', 'kr_market': '한국 장 시간', 'us_market': '미국 장 시간' };
      return map[s] || s || '-';
    },
  },
};
</script>
